/**
 * useExposureFilter - Groups orders and positions by game, cross-referenced
 * against live market data. Returns only games where the user has exposure
 * (open orders or positions).
 */

import { useMemo } from 'react';
import type { Position, KalshiOrder } from '../types';
import type { ConsolidatedGameBooks } from '../lib/nbaConsolidated/types';
import type {
  GameData,
  MoneylineGameData,
  SpreadGameData,
  TotalGameData,
} from '../lib/sportsStream/types';

export interface GameExposure {
  /** Stable key for this game (e.g. "2026-02-23|LAL|BOS") */
  gameKey: string;
  awayCode: string;
  homeCode: string;
  awayName?: string;
  homeName?: string;
  date: string;
  startTimePt?: string | null;

  /** All tickers associated with this game */
  tickers: Set<string>;

  /** User's orders in this game */
  orders: KalshiOrder[];
  /** User's positions in this game */
  positions: Position[];

  /** Moneyline data from consolidated games (if available) */
  moneyline: ConsolidatedGameBooks | null;
  /** Spread data from sports stream (if available) */
  spread: SpreadGameData | null;
  /** Total data from sports stream (if available) */
  total: TotalGameData | null;
}

interface UseExposureFilterParams {
  orders: KalshiOrder[];
  positions: Position[];
  positionGameDateByTicker: Record<string, string>;
  consolidatedGames?: ConsolidatedGameBooks[];
  sportsGames?: Map<string, GameData[]>;
}

/**
 * Extract team codes from a Kalshi ticker. Tickers typically contain
 * team codes as 2-4 letter uppercase fragments, e.g.:
 * "KXNBA-26FEB23-LAL-BOS-..." -> awayCode=LAL, homeCode=BOS
 *
 * Returns null if no match found.
 */
function extractTeamCodesFromTicker(ticker: string): { away: string; home: string } | null {
  // Match pattern: series prefix, date, then two team codes separated by dash
  const match = ticker.match(/^KX[A-Z]+-\d{2}[A-Z]{3}\d{2}-([A-Z]{2,5})-([A-Z]{2,5})/);
  if (match?.[1] && match?.[2]) return { away: match[1], home: match[2] };

  // Polymarket or other formats - try to find two adjacent uppercase groups
  const parts = ticker.split(/[-_]/);
  const codes = parts.filter((p) => /^[A-Z]{2,5}$/.test(p));
  if (codes.length >= 2 && codes[0] && codes[1]) return { away: codes[0], home: codes[1] };

  return null;
}

/**
 * Build a lookup from ticker -> game key using all available market data.
 */
function buildTickerToGameKey(
  consolidatedGames: ConsolidatedGameBooks[],
  sportsGames: Map<string, GameData[]>
): Map<string, string> {
  const map = new Map<string, string>();

  for (const game of consolidatedGames) {
    const key = game.key;
    if (game.kalshi?.markets.away.marketTicker) {
      map.set(game.kalshi.markets.away.marketTicker, key);
    }
    if (game.kalshi?.markets.home.marketTicker) {
      map.set(game.kalshi.markets.home.marketTicker, key);
    }
    if (game.polymarket?.markets.away.marketTicker) {
      map.set(game.polymarket.markets.away.marketTicker, key);
    }
    if (game.polymarket?.markets.home.marketTicker) {
      map.set(game.polymarket.markets.home.marketTicker, key);
    }
  }

  for (const [, games] of sportsGames) {
    for (const game of games) {
      const key = `${game.date}|${game.awayCode.toUpperCase()}|${game.homeCode.toUpperCase()}`;
      if (game.marketType === 'moneyline') {
        const ml = game as MoneylineGameData;
        if (ml.away?.marketTicker) map.set(ml.away.marketTicker, key);
        if (ml.home?.marketTicker) map.set(ml.home.marketTicker, key);
      } else if (game.marketType === 'spread') {
        const sp = game as SpreadGameData;
        for (const [, info] of sp.markets) {
          if (info.book.marketTicker) map.set(info.book.marketTicker, key);
        }
      } else if (game.marketType === 'total') {
        const tot = game as TotalGameData;
        for (const [, book] of tot.markets) {
          if (book.marketTicker) map.set(book.marketTicker, key);
        }
      }
    }
  }

  return map;
}

export function useExposureFilter({
  orders,
  positions,
  positionGameDateByTicker,
  consolidatedGames = [],
  sportsGames = new Map(),
}: UseExposureFilterParams): GameExposure[] {
  return useMemo(() => {
    const tickerToGameKey = buildTickerToGameKey(consolidatedGames, sportsGames);

    const consolidatedByKey = new Map<string, ConsolidatedGameBooks>();
    for (const g of consolidatedGames) {
      consolidatedByKey.set(g.key, g);
    }

    // Index sports games by game key + market type
    const spreadByKey = new Map<string, SpreadGameData>();
    const totalByKey = new Map<string, TotalGameData>();
    for (const [, games] of sportsGames) {
      for (const game of games) {
        const key = `${game.date}|${game.awayCode.toUpperCase()}|${game.homeCode.toUpperCase()}`;
        if (game.marketType === 'spread') spreadByKey.set(key, game as SpreadGameData);
        if (game.marketType === 'total') totalByKey.set(key, game as TotalGameData);
      }
    }

    // Group orders and positions by game key
    const exposureMap = new Map<
      string,
      {
        orders: KalshiOrder[];
        positions: Position[];
        tickers: Set<string>;
      }
    >();

    const activeOrders = orders.filter((o) => o.status === 'resting' || o.status === 'pending');

    function ensureEntry(gameKey: string) {
      if (!exposureMap.has(gameKey)) {
        exposureMap.set(gameKey, { orders: [], positions: [], tickers: new Set() });
      }
      return exposureMap.get(gameKey)!;
    }

    for (const order of activeOrders) {
      let gameKey = tickerToGameKey.get(order.ticker);
      if (!gameKey) {
        // Fallback: try to extract from ticker string
        const codes = extractTeamCodesFromTicker(order.ticker);
        const date = positionGameDateByTicker[order.ticker];
        if (codes && date) {
          gameKey = `${date}|${codes.away}|${codes.home}`;
        }
      }
      if (gameKey) {
        const entry = ensureEntry(gameKey);
        entry.orders.push(order);
        entry.tickers.add(order.ticker);
      }
    }

    for (const pos of positions) {
      if (pos.quantity === 0) continue;
      let gameKey = tickerToGameKey.get(pos.ticker);
      if (!gameKey) {
        const codes = extractTeamCodesFromTicker(pos.ticker);
        const date = positionGameDateByTicker[pos.ticker];
        if (codes && date) {
          gameKey = `${date}|${codes.away}|${codes.home}`;
        }
      }
      if (gameKey) {
        const entry = ensureEntry(gameKey);
        entry.positions.push(pos);
        entry.tickers.add(pos.ticker);
      }
    }

    // Build GameExposure objects
    const result: GameExposure[] = [];
    for (const [gameKey, entry] of exposureMap) {
      const parts = gameKey.split('|');
      if (parts.length < 3) continue;

      const [date, awayCode, homeCode] = parts as [string, string, string];

      const consolidated = consolidatedByKey.get(gameKey) ?? null;
      const spread = spreadByKey.get(gameKey) ?? null;
      const total = totalByKey.get(gameKey) ?? null;

      // Get display names from whichever source has them
      const awayName = consolidated?.awayName ?? spread?.awayName ?? total?.awayName ?? awayCode;
      const homeName = consolidated?.homeName ?? spread?.homeName ?? total?.homeName ?? homeCode;
      const startTimePt = consolidated?.startTimePt ?? spread?.startTimePt ?? total?.startTimePt;

      result.push({
        gameKey,
        awayCode,
        homeCode,
        awayName,
        homeName,
        date,
        startTimePt,
        tickers: entry.tickers,
        orders: entry.orders,
        positions: entry.positions,
        moneyline: consolidated,
        spread,
        total,
      });
    }

    // Sort by date, then game time
    result.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return (a.startTimePt ?? '').localeCompare(b.startTimePt ?? '');
    });

    return result;
  }, [orders, positions, positionGameDateByTicker, consolidatedGames, sportsGames]);
}
